import { z } from "zod";
import { AgentProvider, ClaudeModel, SpeedLevel } from "./agents.ts";

export const HANDLE_RE = /^[a-z][a-z0-9_]{2,31}$/;
export const RESERVED_HANDLES = ["team", "all", "system", "admin", "builder", "codvero", "me", "user", "agent", "bot"];

export const AgentStatus = z.enum(["DRAFT", "ACTIVE", "DISABLED", "ARCHIVED"]);
export type AgentStatus = z.infer<typeof AgentStatus>;

export const AgentInstructions = z.object({
  goal: z.string().max(2000).default(""),
  responsibilities: z.string().max(4000).default(""),
  constraints: z.string().max(4000).default(""),
  workStyle: z.string().max(2000).default(""),
  outputs: z.string().max(2000).default(""),
  systemPrompt: z.string().max(8000).default(""),
  forbidden: z.string().max(4000).default(""),
});
export type AgentInstructions = z.infer<typeof AgentInstructions>;

export const AgentModelConfig = z.object({
  /** Which CLI runs this agent; the account is the project's connected account for that provider. */
  provider: AgentProvider.default("claude"),
  /** "default" = the account's default; otherwise an alias (fable/opus/…) or a full model id from the catalog. */
  model: ClaudeModel.default("default"),
  speed: SpeedLevel.default("standard"),
  thinking: z.enum(["auto", "low", "medium", "high", "max"]).default("auto"),
  timeoutSec: z.number().int().min(60).max(7200).default(1800),
  maxAttempts: z.number().int().min(1).max(5).default(2),
  maxTurns: z.number().int().min(1).max(500).default(100),
});
export type AgentModelConfig = z.infer<typeof AgentModelConfig>;

export const AgentPermissions = z.object({
  read: z.boolean().default(true),
  edit: z.boolean().default(false),
  run: z.boolean().default(false),
  network: z.boolean().default(false),
  browser: z.boolean().default(false),
  secrets: z.boolean().default(false),
  writeOutside: z.boolean().default(false),
  integrations: z.boolean().default(false),
  approvals: z
    .object({ edits: z.boolean().default(false), terminal: z.boolean().default(false), internet: z.boolean().default(false), secrets: z.boolean().default(true), handoff: z.boolean().default(false) })
    .prefault({}),
});
export type AgentPermissions = z.infer<typeof AgentPermissions>;

/** Emoji + color, optionally a small photo (data URL, cropped client-side to 96px, ≤ 120 KB). */
export const AgentAvatar = z.object({ emoji: z.string(), color: z.string(), image: z.string().nullable().optional() });
export type AgentAvatar = z.infer<typeof AgentAvatar>;
export const AgentAvatarInput = z.object({
  emoji: z.string().max(8).default("🤖"),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#2f9e7a"),
  image: z.string().regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/).max(160_000).nullable().default(null),
});

/** Mentioning @all queues one task per active agent of the project, in order. */
export const ALL_HANDLE = "all";

export const Agent = z.object({
  id: z.string(),
  ownerId: z.string(),
  handle: z.string(),
  name: z.string(),
  title: z.string(),
  description: z.string(),
  avatar: AgentAvatar,
  language: z.enum(["ar", "en"]),
  status: AgentStatus,
  projectIds: z.array(z.string()),
  instructions: AgentInstructions,
  model: AgentModelConfig,
  permissions: AgentPermissions,
  skillIds: z.array(z.string()),
  version: z.number(),
  tasks: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Agent = z.infer<typeof Agent>;

export const AgentInput = z.object({
  handle: z.string().trim().toLowerCase().regex(HANDLE_RE, "3-32 chars: a-z, 0-9, _ ; must start with a letter"),
  name: z.string().trim().min(1).max(80),
  title: z.string().trim().max(80).default(""),
  description: z.string().trim().max(500).default(""),
  avatar: AgentAvatarInput.prefault({}),
  language: z.enum(["ar", "en"]).default("ar"),
  status: AgentStatus.default("DRAFT"),
  projectIds: z.array(z.string()).max(50).default([]),
  instructions: AgentInstructions.prefault({}),
  model: AgentModelConfig.prefault({}),
  permissions: AgentPermissions.prefault({}),
  skillIds: z.array(z.string()).max(50).default([]),
});
export type AgentInput = z.infer<typeof AgentInput>;

export const AgentGroup = z.object({
  id: z.string(),
  ownerId: z.string(),
  handle: z.string(),
  name: z.string(),
  description: z.string(),
  leadAgentId: z.string().nullable(),
  memberIds: z.array(z.string()),
  projectIds: z.array(z.string()),
  createdAt: z.string(),
});
export type AgentGroup = z.infer<typeof AgentGroup>;
export const GroupInput = z.object({
  handle: z.string().trim().toLowerCase().regex(HANDLE_RE),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).default(""),
  leadAgentId: z.string().nullable().default(null),
  memberIds: z.array(z.string()).max(30).default([]),
  projectIds: z.array(z.string()).max(50).default([]),
});
export type GroupInput = z.infer<typeof GroupInput>;

/** The five roles seeded on first visit, like the original's MAS Team draft profiles. */
/** Capabilities each seeded role starts with: only the engineer changes files and runs commands. */
export const SEED_PERMISSIONS: Record<string, Partial<Omit<AgentPermissions, "approvals">>> = {
  team_lead: { read: true, network: true },
  product_manager: { read: true },
  system_architect: { read: true },
  engineer: { read: true, edit: true, run: true, network: true, browser: true },
  designer: { read: true, edit: true, run: true, browser: true },
  data_analyst: { read: true, run: true },
};

/** The company workflow each seeded role follows. Editable per agent in the Studio wizard. */
export const SEED_INSTRUCTIONS: Record<string, Partial<AgentInstructions>> = {
  team_lead: {
    goal: "تستقبل فكرة المشروع من المالك، تحلّلها وتعيد صياغتها وتطوّرها إلى وصف واضح بالمتطلبات والنطاق ومعايير النجاح، ثم تسلّمها لمدير المنتج.",
    responsibilities: "1) افهم الفكرة واسأل نفسك ما الذي لم يُقل. 2) اكتب: الهدف، المستخدمون، المزايا الأساسية، ما هو خارج النطاق، معايير القبول. 3) سلّم كل ذلك لمدير المنتج فقط بسطر HANDOFF واحد. 4) عندما يعود إليك تقرير الإنجاز، راجعه، واكتب للمالك تسليماً نهائياً واضحاً (ما أُنجز، كيف يُشغَّل، ما بقي)، ثم قسماً بعنوان «للذاكرة» يلخّص القرارات والبنية ليعود إليها الفريق لاحقاً.",
    constraints: "لا تنفّذ العمل بنفسك ولا توزّعه على الأفراد مباشرة؛ التوزيع مهمة مدير المنتج.",
    outputs: "وثيقة متطلبات مطوّرة، ثم تسليم نهائي + قسم «للذاكرة».",
  },
  product_manager: {
    goal: "تحوّل وثيقة قائد الفريق إلى مهام محددة للأدوار المطلوبة فقط، بالترتيب الصحيح.",
    responsibilities: "1) حدّد الأدوار اللازمة فعلاً (تصميم فقط؟ برمجة ثم تصميم؟ تحليل بيانات؟). 2) اكتب لكل دور مهمة واضحة بسطر HANDOFF مستقل، مرتبةً بترتيب التنفيذ؛ المهام تُنفَّذ واحدة تلو الأخرى وكل مهمة ترى نتائج ما سبقها تلقائياً. 3) عندما تنتهي كل المهام يصلك تقرير آلي بنتائجها: راجعها، وإن وجدت نقصاً أعد التسليم بمهام إصلاح، وإلا اكتب تقرير إنجاز موجزاً (ما أُنجز، الملفات، كيف يُشغَّل، الملاحظات) ليصل لقائد الفريق.",
    constraints: "لا تنفّذ بنفسك. لا تسلّم لدور لا يحتاجه المشروع.",
  },
  system_architect: { goal: "تقترح البنية التقنية وهيكل الملفات والقرارات التقنية عندما يطلبها مدير المنتج.", outputs: "وثيقة قصيرة: التقنيات، هيكل الملفات، تدفق البيانات، المخاطر." },
  engineer: { goal: "تنفّذ المهمة البرمجية المسندة إليك داخل /workspace كاملةً وتتحقق منها.", responsibilities: "اكتب الكود، شغّله، اختبره، وافتح الواجهة في المتصفح إن كانت ويب. اذكر في تقريرك الملفات وكيفية التشغيل.", constraints: "لا تعِد توزيع العمل؛ أنجزه أو اذكر بوضوح ما تعذّر ولماذا." },
  designer: { goal: "تصمّم وتنفّذ واجهة المستخدم والهوية البصرية (HTML/CSS، التخطيط، الألوان، الخطوط، RTL) على ما بناه المهندس أو من الصفر إن طُلب.", responsibilities: "طبّق التصميم في الملفات مباشرة، تحقق منه في المتصفح على مقاسات الجوال والحاسوب، واذكر ما غيّرته.", constraints: "لا تغيّر منطق البرمجة إلا بقدر ما يلزم للتصميم." },
  data_analyst: { goal: "تتحقق من البيانات وحالات الاختبار وتنتج التحليلات المطلوبة.", outputs: "جداول حالات، نتائج تحقق، ملاحظات بالأرقام." },
};
export const SEED_AGENTS: (Pick<AgentInput, "handle" | "name" | "title" | "description"> & { avatar: { emoji: string; color: string; image: null } })[] = [
  { handle: "team_lead", name: "قائد الفريق", title: "Team Leader", description: "ينسّق الفريق ويوزّع المهام ويراجع النتيجة النهائية.", avatar: { emoji: "🧭", color: "#2f9e7a", image: null } },
  { handle: "product_manager", name: "مدير المنتج", title: "Product Manager", description: "يحوّل الطلب المعتمد إلى متطلبات ونطاق واضحين.", avatar: { emoji: "📋", color: "#3b82f6", image: null } },
  { handle: "system_architect", name: "المعماري", title: "Architect", description: "يحدد تصميم النظام وتدفق البيانات والواجهات والحدود التقنية.", avatar: { emoji: "🏗️", color: "#8b5cf6", image: null } },
  { handle: "engineer", name: "المهندس", title: "Engineer", description: "ينفّذ التطبيق المعتمد داخل مساحة العمل المعزولة.", avatar: { emoji: "⚙️", color: "#f59e0b", image: null } },
  { handle: "designer", name: "المصمم", title: "UI/UX Designer", description: "يصمّم الواجهة والهوية البصرية وينفّذها في الملفات.", avatar: { emoji: "🎨", color: "#ec4899", image: null } },
  { handle: "data_analyst", name: "محلل البيانات", title: "Data Analyst", description: "يتحقق من متطلبات البيانات وينتج تحليلات المشروع.", avatar: { emoji: "📊", color: "#06b6d4", image: null } },
];

// ---------------- skills ----------------

export const SkillPermissionKeys = ["run", "network", "edit", "secrets", "browser"] as const;
export type SkillPermissionKey = (typeof SkillPermissionKeys)[number];
export const SkillManifest = z.object({
  files: z.array(z.object({ path: z.string(), size: z.number() })),
  totalBytes: z.number(),
  requested: z.record(z.enum(SkillPermissionKeys), z.boolean()),
  allowedTools: z.array(z.string()),
  warnings: z.array(z.string()),
  skillMd: z.string(),
});
export type SkillManifest = z.infer<typeof SkillManifest>;
export const SkillReview = z.object({
  approved: z.boolean(),
  granted: z.partialRecord(z.enum(SkillPermissionKeys), z.boolean()),
  note: z.string().max(500).default(""),
  reviewedAt: z.string(),
  /** An update kept this approval but the new version asks for these permissions it did not ask for before. */
  newPermissions: z.array(z.enum(SkillPermissionKeys)).optional(),
});
export type SkillReview = z.infer<typeof SkillReview>;
export const SkillScope = z.enum(["studio", "chat"]);
/** Whether a provider can use a Chat skill, and why not ("named": its compatibility field names other providers;
 *  "claude_only": it relies on Claude Code features; "no_mcp": it calls MCP tools and the provider has no MCP;
 *  "mcp_missing" / "mcp_unbound": the MCP connection it calls is not set up, or not for this provider). */
export const SkillSupport = z.object({
  ok: z.boolean(),
  reasons: z.array(z.object({ code: z.enum(["named", "claude_only", "no_mcp", "mcp_missing", "mcp_unbound"]), detail: z.string() })),
});
export type SkillSupport = z.infer<typeof SkillSupport>;
export type SkillScope = z.infer<typeof SkillScope>;
export const Skill = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  source: z.enum(["zip", "github"]),
  sourceRef: z.string().nullable(),
  version: z.number(),
  status: z.enum(["uploaded", "reviewed", "archived"]),
  manifest: SkillManifest,
  review: SkillReview.nullable(),
  /** the collection this skill was imported with; null = standalone */
  collectionId: z.string().nullable(),
  /** "studio": for studio agents; "chat": for the Chat section's providers */
  scope: SkillScope,
  /** Chat skills: the providers that get it (null = every provider) */
  providers: z.array(AgentProvider).nullable(),
  /** Chat skills: which providers can use it (an unsupported provider never gets it) */
  support: z.partialRecord(AgentProvider, SkillSupport).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Skill = z.infer<typeof Skill>;
export const SetSkillProvidersInput = z.object({ providers: z.array(AgentProvider).min(1).nullable() });

/** Skills imported together: one GitHub repository, or one archive with several skills. */
export const SkillCollection = z.object({
  id: z.string(),
  name: z.string(),
  source: z.enum(["zip", "github"]),
  /** github only */
  url: z.string().nullable(),
  repo: z.string().nullable(),
  ref: z.string().nullable(),
  commitSha: z.string().nullable(),
  latestSha: z.string().nullable(),
  checkedAt: z.string().nullable(),
  /** a newer commit exists on the branch than the one imported */
  updateAvailable: z.boolean(),
  skillCount: z.number(),
  scope: SkillScope,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SkillCollection = z.infer<typeof SkillCollection>;
export const CollectionUpdateResult = z.object({ collection: SkillCollection, added: z.number(), updated: z.number(), unchanged: z.number(), archived: z.number() });
export type CollectionUpdateResult = z.infer<typeof CollectionUpdateResult>;
export const ReviewSkillInput = z.object({ approved: z.boolean(), granted: z.partialRecord(z.enum(SkillPermissionKeys), z.boolean()).default({}), note: z.string().max(500).default("") });
export type ReviewSkillInput = z.infer<typeof ReviewSkillInput>;
export const ImportGithubInput = z.object({ url: z.string().url().max(300), ref: z.string().max(100).optional() });

// ---------------- team tasks ----------------

export const TeamTaskStatus = z.enum(["PENDING_APPROVAL", "QUEUED", "RUNNING", "DONE", "FAILED", "REJECTED", "CANCELLED"]);
/** work = a delegated task; report = an automatic "your delegated work finished, review and report up" task. */
export const TeamTaskKind = z.enum(["work", "report"]);
export type TeamTaskStatus = z.infer<typeof TeamTaskStatus>;
export const TeamTask = z.object({
  id: z.string(),
  projectId: z.string(),
  chatId: z.string(),
  agentId: z.string(),
  agentHandle: z.string(),
  agentName: z.string(),
  agentAvatar: AgentAvatar,
  groupId: z.string().nullable(),
  requestedBy: z.string(),
  parentTaskId: z.string().nullable(),
  depth: z.number(),
  prompt: z.string(),
  status: TeamTaskStatus,
  kind: TeamTaskKind,
  gates: z.array(z.string()),
  runId: z.string().nullable(),
  result: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
});
export type TeamTask = z.infer<typeof TeamTask>;

/** Mentionable handles in a project chat: the requester's agents (scoped) and groups. */
export const Mentionable = z.object({ kind: z.enum(["agent", "group", "all"]), id: z.string(), handle: z.string(), name: z.string(), avatar: AgentAvatar.nullable(), status: z.string().nullable() });
export type Mentionable = z.infer<typeof Mentionable>;
export const MENTION_RE = /(^|[\s(（])@([a-z][a-z0-9_]{2,31})\b/g;
export const HANDOFF_RE = /^\s*HANDOFF:\s*@([a-z][a-z0-9_]{2,31})\s*[-—:]\s*(.+)$/gim;
